import type { Id } from '@e-socna/data-layer';

type OptionableGroup = Readonly<{
  _id: Id;
  isOptional: boolean;
  label?: string | null;
  subMissionId?: Id | null;
}>;

export type QuotationOptionGroup = Readonly<{ id: Id; label: string }>;

const SECTION_SEPARATOR = ' — ';

// The client chooses among the optional groups of the frozen quotation when
// there is one, otherwise among those of the live pricing. The groups of the
// sub-missions of a collaboration are prefixed by their sub-mission.
export const getQuotationOptionGroups = ({
  liveGroups,
  snapshotGroups,
  subMissionTitles,
}: Readonly<{
  liveGroups: readonly OptionableGroup[];
  snapshotGroups?: readonly OptionableGroup[] | null;
  subMissionTitles?: ReadonlyMap<Id, string>;
}>): readonly QuotationOptionGroup[] =>
  (snapshotGroups ?? liveGroups)
    .filter((group) => group.isOptional)
    .map((group) => ({
      id: group._id,
      label: [
        group.subMissionId ? subMissionTitles?.get(group.subMissionId) : null,
        group.label,
      ]
        .filter(Boolean)
        .join(SECTION_SEPARATOR),
    }));

// A previous client choice may reference groups that are no longer options
// (made mandatory, deleted, or regenerated by a customize): they are dropped,
// since the back rejects them.
export const getInitialSelectedOptionGroupIds = ({
  initialSelectedOptionGroupIds,
  optionGroups,
}: Readonly<{
  initialSelectedOptionGroupIds?: readonly Id[] | null;
  optionGroups: readonly QuotationOptionGroup[];
}>): readonly Id[] => {
  const optionGroupIds = optionGroups.map(({ id }) => id);

  if (!initialSelectedOptionGroupIds) {
    return optionGroupIds;
  }

  return initialSelectedOptionGroupIds.filter((id) =>
    optionGroupIds.includes(id),
  );
};
